import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Tooltip } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { reportDeleteById, reportDeleteByIds, reportExport } from 'api/endpoints/report';
import Button from 'components/Button';
import Table, { IndeterminateCheckbox } from 'components/Table';
import { ENUM_TYPE_DOWNLOAD } from 'constants/enum';
import { FORMAT_TIME } from 'constants/index';
import { TEXT_COMMON, TEXT_DOWNLOAD, TEXT_REPORT_LIST } from 'constants/string/texts';
import { useClickOutside } from 'hooks/useClickOutside';
import { toastError, toastSuccess } from 'lib/toast';
import AppRoutes from 'routes/routes';
import { clearModal, setLoading, setModal } from 'store/actions/common';

const ControlReportItem = ({ item, deleteReport }) => {
  const dispatch = useDispatch();
  const [openControlReportItem, setIsOpenControlReportItem] = useState(false);
  const controlReportItemRef = useRef(null);
  const { me } = useSelector((state) => state.auth);

  useClickOutside(controlReportItemRef, () => setIsOpenControlReportItem(false));

  const handleDownload = async (export_type) => {
    dispatch(setLoading({ isOpen: true }));
    await reportExport({ report_ids: [item.id] }, export_type, me.lang)
      .then((res) => {
        dispatch(setLoading({ isOpen: false }));
      })
      .catch((error) => {
        toastError(error.message);
        dispatch(setLoading({ isOpen: false }));
      });
  };

  return (
    <div className="flex-center-center text-sm">
      <div className="relative flex justify-center items-center">
        <button type="button" className="w-6 h-8" onClick={() => setIsOpenControlReportItem((prev) => !prev)} ref={controlReportItemRef}>
          <img src={require('assets/resources/MoreVert.svg').default} alt="more vert icon" width={24} height={24} />
        </button>
        <div className={`absolute z-2 right-0 py-1 bg-white shadow-soft-md animation-dropdown ${openControlReportItem ? 'show' : ''}`}>
          <ul>
            <li
              onClick={() => handleDownload(ENUM_TYPE_DOWNLOAD.EXCEL)}
              className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
            >
              {TEXT_DOWNLOAD.EXCEL_DOWNLOAD}
            </li>
            <li
              onClick={() => handleDownload(ENUM_TYPE_DOWNLOAD.CSV)}
              className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
            >
              {TEXT_DOWNLOAD.CSV_DOWNLOAD}
            </li>
            <li
              onClick={() => {
                deleteReport(item.id);
              }}
              className="px-3 w-48 py-1.75 text-danger-229 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
            >
              {TEXT_COMMON.DELETE}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const ReportListTable = ({ data, setSort, reloadData, sort, page, keyword, isFilter, fromDate, toDate }) => {
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([{ page: page, selected: [] }]);

  const handleRowSelect = (rowSelect) => {
    try {
      setSelectedRows((prevSelected) => {
        // Get item index with page number
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        // Create new object and push to array
        if (pageSelectedExists === -1) {
          return [
            ...prevSelected,
            {
              page: page,
              selected: [rowSelect?.original?.id],
            },
          ];
        }

        const rowIsSelected = selectedRowWithPage[pageSelectedExists]?.selected?.find((row) => row === rowSelect?.original?.id);

        /**
         * If you are select
         * - Delete the selected item from the array
         * - Delete all Object contains lines
         * - Add new object
         */
        if (rowIsSelected) {
          const selectedRemovedRow = selectedRowWithPage[pageSelectedExists].selected.filter((row) => row !== rowSelect.original.id);
          selectedRowWithPage.splice(pageSelectedExists, 1);
          selectedRowWithPage.push({
            page: page,
            selected: selectedRemovedRow,
          });
          return selectedRowWithPage;
        }

        const rowSelected = selectedRowWithPage[pageSelectedExists].selected;
        selectedRowWithPage.splice(pageSelectedExists, 1);
        selectedRowWithPage.push({
          page: page,
          selected: [...rowSelected, rowSelect.original.id],
        });
        return selectedRowWithPage;
      });
      rowSelect.getToggleSelectedHandler();
    } catch {
      // Handle error select
    }
  };

  const handleSelectAll = (table) => {
    try {
      setSelectedRows((prevState) => {
        const dataRows = table.getRowModel().rows;
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        if (pageSelectedExists === -1) {
          return [
            ...prevState,
            {
              page: page,
              selected: dataRows?.map((row) => row?.original?.id),
            },
          ];
        }

        /**
         * If the Select of the page is currently in the Testhall (Condition) state
         * (Condition): Select length is equal to the number of non -public status items on the current page
         * then remove the whole object of that page to become Uncheck.
         */
        if (selectedRowWithPage[pageSelectedExists]?.selected?.length === dataRows?.length) {
          selectedRowWithPage.splice(pageSelectedExists, 1);
          return selectedRowWithPage;
        }

        selectedRowWithPage.splice(pageSelectedExists, 1);
        selectedRowWithPage.push({
          page: page,
          selected: dataRows.map((row) => row?.original?.id),
        });

        return selectedRowWithPage;
      });
    } catch {
      // Handle error select all
    }
  };

  /**
   * Delete a report
   * @param {*} idReport
   */
  const onClickDeleteReport = (idReport) => {
    const confirmDeleteReport = async () => {
      try {
        dispatch(setLoading({ isOpen: true }));
        await reportDeleteById(idReport)
          .then(() => {
            reloadData();
            toastSuccess(`1${TEXT_REPORT_LIST.SELECTED_DELETED}`);
            dispatch(clearModal());
            setSelectedRows(() => {
              const selectedRowWithPage = selectedRows.concat();

              const pageSelectedExists = _.findIndex(selectedRowWithPage, { page: page });
              if (pageSelectedExists !== -1) {
                const selectedRemovedRow = selectedRowWithPage[pageSelectedExists]?.selected?.filter((row) => row !== idReport);
                selectedRowWithPage?.splice(pageSelectedExists, 1);

                selectedRowWithPage?.push({
                  page: page,
                  selected: selectedRemovedRow,
                });
                return selectedRowWithPage;
              }

              return [];
            });
          })
          .catch((err) => {
            toastError(err?.message);
            dispatch(clearModal());
          });
        dispatch(setLoading({ isOpen: false }));
      } catch {
        // Handle Error
        dispatch(setLoading({ isOpen: false }));
      }
    };

    dispatch(
      setModal({
        isOpen: true,
        type: 'delete',
        title: TEXT_REPORT_LIST.CONFIRM_DELETE_SURVEY,
        confirmText: TEXT_COMMON.DELETE,
        cancelText: TEXT_COMMON.CANCEL_TEXT,
        onConfirm: confirmDeleteReport,
        onCancel: () => {
          dispatch(clearModal());
        },
      })
    );
  };

  /**
   * Delete multiple report with selected
   */
  const onClickDeleteReports = () => {
    const confirmDeleteReports = async () => {
      try {
        dispatch(setLoading({ isOpen: true }));
        await reportDeleteByIds({ ids: listReportsDelete })
          .then(() => {
            reloadData();
            toastSuccess(`${listReportsDelete.length}${TEXT_REPORT_LIST.SELECTED_DELETED}`);
            dispatch(clearModal());
            setSelectedRows([]);
          })
          .catch((err) => {
            toastError(err?.message);
            dispatch(clearModal());
          });
        dispatch(setLoading({ isOpen: false }));
      } catch {
        // Handle error
        dispatch(setLoading({ isOpen: false }));
      }
    };

    const listReportsDelete = _.flatMap(selectedRows, 'selected');
    dispatch(
      setModal({
        isOpen: true,
        type: 'delete',
        title: TEXT_REPORT_LIST.CONFIRM_DELETE_SURVEY,
        confirmText: TEXT_COMMON.DELETE,
        cancelText: TEXT_COMMON.CANCEL_TEXT,
        onConfirm: confirmDeleteReports,
        onCancel: () => {
          dispatch(clearModal());
        },
      })
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: '-',
        header: ({ table }) => (
          <div className={`${data?.length <= 0 && '!cursor-not-allowed'} flex justify-center items-center w-4`}>
            <IndeterminateCheckbox
              className={`m-auto ${data?.length <= 0 && '!cursor-not-allowed'}`}
              {...{
                checked: _.find(selectedRows, { page: page })?.selected?.length === table.getRowModel().rows?.length && data?.length > 0,
                indeterminate: false,
                onChange: () => handleSelectAll(table),
              }}
            />
          </div>
        ),
        size: 48,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className="flex justify-center">
              <IndeterminateCheckbox
                className={'m-auto'}
                {...{
                  checked: _.find(selectedRows, { page: page })?.selected?.some((item) => item === row?.original?.id),
                  indeterminate: false,
                  onChange: () => handleRowSelect(row),
                }}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'child_id',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{TEXT_REPORT_LIST.USER_ID}</span>;
        },
        cell: ({ getValue }) => {
          return (
            <div className="flex-between-center text-sm gap-5">
              <span className="break-all">{getValue()}</span>
            </div>
          );
        },
        size: 379,
        sortingFn: () => {},
      },
      {
        accessorKey: 'link',
        size: 405,
        header: () => <span>{TEXT_REPORT_LIST.DIAGNOSIS}</span>,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className="flex items-center w-92">
              <div className="flex items-center justify-between w-full">
                {row?.original?.has_sqd_snapshot && (
                  <Link to={AppRoutes.reportSDQFn(row.original.id)} className="text-sm text-primary-346">
                    <div>
                      <Tooltip
                        placement="bottom"
                        overlayStyle={{ maxWidth: '300px' }}
                        overlayInnerStyle={{ fontSize: '14px', background: 'rgba(0, 0, 0, 0.75)' }}
                        showArrow={false}
                        title={`${TEXT_REPORT_LIST.SDQ_TEXT}・${TEXT_REPORT_LIST.REPORT}`}
                      >
                        <span className={'text-sm text-primary-346 break-all line-clamp-1'}>
                          {TEXT_REPORT_LIST.SDQ_TEXT}・{TEXT_REPORT_LIST.REPORT}
                        </span>
                      </Tooltip>
                    </div>
                  </Link>
                )}
                {row?.original?.has_exam_snapshot && (
                  <Link to={AppRoutes.reportViewFn(row.original.id)} className="flex">
                    <div>
                      <Tooltip
                        placement="bottom"
                        overlayStyle={{ maxWidth: '300px' }}
                        overlayInnerStyle={{ fontSize: '14px', background: 'rgba(0, 0, 0, 0.75)' }}
                        showArrow={false}
                        title={
                          row?.original?.has_analysis_content
                            ? `${row.original.exam_title}・${TEXT_REPORT_LIST.REPORT}`
                            : TEXT_REPORT_LIST.LOCAL_GOVERNMENT_SURVEY
                        }
                      >
                        {row?.original?.has_analysis_content ? (
                          <span className={'text-sm text-primary-346 break-all line-clamp-1'}>
                            {row.original.exam_title}・{TEXT_REPORT_LIST.REPORT}
                          </span>
                        ) : (
                          <span className={'text-sm text-primary-346 break-all line-clamp-1'}>{TEXT_REPORT_LIST.LOCAL_GOVERNMENT_SURVEY}</span>
                        )}
                      </Tooltip>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'created_at',
        size: 144,
        header: () => <span>{TEXT_REPORT_LIST.ANSWER_DAY}</span>,
        cell: ({ row }) => <span>{moment(row.original.created_at).format(FORMAT_TIME.DATE)}</span>,
        sortingFn: () => {},
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        enableSorting: false,
        size: 80,
        cell: ({ row }) => <ControlReportItem deleteReport={onClickDeleteReport} item={row?.original || {}} />,
      },
    ],
    [selectedRows, page]
  );

  useEffect(() => {
    setSelectedRows([]);
  }, [sort, keyword, fromDate, toDate]);

  return (
    <>
      {_.flatMap(selectedRows, 'selected').length > 0 && (
        <>
          <div className="p-4 w-full border-2 border-solid border-primary-423 rounded-md mb-2 flex justify-between items-center h-16">
            <p className="text-secondary-354 text-sm">
              {_.flatMap(selectedRows, 'selected').length} {TEXT_REPORT_LIST.SELECTING}
            </p>
            <div className="flex">
              <div className="flex gap-4">
                <Button
                  className="text-sm w-21 h-8"
                  color="danger-outline"
                  text={TEXT_COMMON.DELETE}
                  onClick={onClickDeleteReports}
                  icon={<img className="mr-2.5" src={require('assets/resources/Trash.svg').default} alt="trash icon" width={14} height={14} />}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <Table columns={columns} data={data} isFilter={isFilter} isReport={true} />
    </>
  );
};

export default React.memo(ReportListTable);
